import type { MouseEvent } from "react";
import { Box, Tooltip, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import CrmMemberAvatar from "../org/CrmMemberAvatar";
import { CRM_HANDLER_FACE, CRM_HANDLER_RING, crmHandlerSlot } from "./crmHandlerMetrics";
import type { CrmHandlerBadgeSize } from "./crmHandlerMetrics";
import type { CrmHandlerView } from "./crmHandlerFace";

/**
 * WHO HANDLES THIS PERSON FOR THE AGENCY — one face, everywhere.
 *
 * ── PURE AND HOOKLESS, and that is load-bearing ─────────────────────────────────
 *
 * It renders in fifty table rows, fifty cards, an autocomplete's option list and four
 * dialogs. `useOrgDirectory` is a module cache with a SUBSCRIBER SET, so resolving the face
 * in here would mount fifty subscriptions to it — the same reason `CrmContactTableRow` takes
 * `ownerFace` as a prop instead of looking it up. The caller resolves once, per page, and
 * hands the value down; `crmHandlerView` is the one resolver.
 *
 * Being value-shaped is also what lets it render from a `CrmContactRow`, from a legacy
 * `RealEstateContact` and from a bare phone string — the three shapes its surfaces are
 * actually on. `NewContactPicker`, `EditContactDialog` and `CalendarCustomerCard` have no
 * `CrmContactRow` anywhere near them.
 *
 * ── ⚠️ `undefined` IS NOT `null`, AND CONFUSING THEM IS THE WHOLE BUG ───────────
 *
 * `undefined` — the lookup has not answered (pending, failed, or never issued because the
 * caller has no agency). `null` — it answered, and nobody is designated.
 *
 * A single nullable would make "we don't know" and "nobody" the same pixel, and the two have
 * opposite consequences: the first must render a reserved blank so nothing moves when the
 * answer lands a beat later, and the second is a real state a user may act on. Getting it
 * wrong shows an empty seat that is actually taken.
 *
 * ── THE RING COLOUR IS THE INFORMATION ─────────────────────────────────────────
 *
 * For a member on the fleet defaults (`{ pool: 'basic', browse: false }`) this badge is the
 * ONLY cross-agent fact on the row — the holders stack is never fetched and the owner column
 * does not exist on their own tab — so nothing beside it explains it and there is no agency
 * panel it can open. It has to answer "is this mine to act on" at a glance. Green = me,
 * amber = a colleague. Deliberately NOT red: this is coordination, not an error.
 *
 * ── NOT A CLICK TARGET IN A LIST ───────────────────────────────────────────────
 *
 * `onAssign` is passed only where a LABEL fits — the panels, the detail page, the row menu.
 * In a 60px cell a bare dashed `+` is not self-explanatory, and for the members who may not
 * assign it would be an affordance that swallows the tap which would otherwise open the row.
 * `CrmContactTableRow` documents that swallowing the row's click is the active harm.
 * Withheld, never greyed.
 */
export type CrmHandlerBadgeProps = {
  /** `undefined` = not answered. `null` = answered, nobody designated. */
  handler?: CrmHandlerView | null;
  size?: CrmHandlerBadgeSize;
  /** `face` = the circle alone (rows, cells, options). `chip` = face + name (panels, dialogs). */
  variant?: "face" | "chip";
  /** Present ⇒ the caller may designate. Absent ⇒ the invitation is OMITTED, not disabled. */
  onAssign?: () => void;
  /** Hold the box when there is nothing to draw, so a late answer moves no pixel. */
  reserve?: boolean;
  /** Overrides the tooltip and the accessible name. */
  label?: string;
};

export default function CrmHandlerBadge({
  handler,
  size = "sm",
  variant = "face",
  onAssign,
  reserve = true,
  label
}: CrmHandlerBadgeProps): JSX.Element | null {
  const theme = useTheme();
  const slot = crmHandlerSlot(size);

  const blank = reserve ? <Box aria-hidden sx={{ width: slot, height: slot, flexShrink: 0 }} /> : null;

  if (handler === undefined) return blank;

  if (handler === null) {
    if (!onAssign) return blank;
    // The product's existing unset-value idiom — `StatusPlaceholderLabel` in
    // `ContactDetailHeader`. Deliberately NOT a `Label`: an empty one collapses to a bare
    // grey circle that reads as a rendering fault, which is the measured reason that
    // placeholder is a bespoke span rather than a `Label` in the first place.
    return (
      <Box
        component="button"
        type="button"
        onClick={(event: MouseEvent) => {
          // Every host of this is inside a clickable row or card.
          event.stopPropagation();
          onAssign();
        }}
        aria-label="שיוך מתווך מטפל"
        sx={{
          height: 22,
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
          px: 0.75,
          border: `1px dashed ${alpha(theme.palette.grey[500], 0.48)}`,
          bgcolor: alpha(theme.palette.grey[500], 0.08),
          borderRadius: "8px",
          whiteSpace: "nowrap",
          color: "text.secondary",
          fontSize: theme.typography.pxToRem(12),
          fontWeight: theme.typography.fontWeightBold,
          font: "inherit",
          cursor: "pointer",
          flexShrink: 0
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 14 }} />
        <span>מתווך מטפל</span>
      </Box>
    );
  }

  const title =
    label ??
    (handler.stale
      ? `מתווך מטפל קודם: ${handler.name} — כבר לא במשרד`
      : handler.isMe
      ? "מתווך מטפל: אני"
      : `מתווך מטפל במשרד: ${handler.name}`);

  return (
    <Tooltip title={title}>
      <Box
        aria-label={title}
        sx={{
          display: "flex",
          // `row`, never `row-reverse` — stylis-plugin-rtl does NOT rewrite
          // `flex-direction`, so `row` is the one formulation that follows the inherited
          // `direction` in both builds. See `CrmHolderAvatars`'s header for the measured
          // inverted-stack bug this avoids.
          flexDirection: "row",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexShrink: 0,
            borderRadius: "50%",
            // ADDS to the footprint, which is why `crmHandlerSlot` exists and why the column
            // width is imported rather than written here.
            boxSizing: "content-box",
            border: `${CRM_HANDLER_RING}px solid`,
            // A departed handler is a fact, not a warning — nobody can act on it but an
            // admin, and it must not read as urgent on every row of a stale agency.
            borderColor: handler.stale ? "grey.400" : handler.isMe ? "success.main" : "warning.main"
          }}
        >
          <CrmMemberAvatar name={handler.name} avatarUrl={handler.avatarUrl} size={CRM_HANDLER_FACE[size]} />
        </Box>

        {variant === "chip" && (
          <Typography variant="caption" noWrap sx={{ minWidth: 0, color: "text.secondary", fontSize: 11.5, lineHeight: 1.4 }}>
            {handler.isMe && !handler.stale ? "אני" : handler.name}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
